import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppContext from 'AppContext';
import { ChevronDown } from 'lucide-react';
import { up } from 'styled-breakpoints';
import styled, { css, keyframes } from 'styled-components';
import Link from 'components/base/Link';

const overviewTimelineHash = '#highlightedcontainer';

const ItemAnimation = keyframes`
  0% {
    opacity: 0;
    max-height: 40px;
    max-width: 100%;
    opacity: 0;
  }
  100% {
    max-height: 40px;
    max-width: 100%;
    opacity: 1;
  }`;

const StyledTag = styled.span`
  border-radius: 4px;
  background: ${(props) => props.theme.colors.white};
  color: ${(props) => props.color};
  padding: 13px 21px;
  transition: padding, 0.2s;
  display: inline-block;
  text-align: center;
  text-transform: uppercase;
  font-family: 'pt sans';
  font-weight: 600 !important;
  text-transform: none;
  box-sizing: border-box;
  width: 100%;
  box-shadow: ${(props) =>
    props.isMobileNav
      ? 'none'
      : `0px 2px 8px ${props.theme.colors.gray11}`} !important;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 1px;
  @media screen and (max-width: 820px) {
    font-size: 14px;
    padding: 15px;
    transition: padding, 0.2s;
  }

  ${(props) =>
    props.isActive &&
    css`
      background: ${props.theme.colors.black2};
      color: ${props.theme.colors.white};
    `}

  ${(props) =>
    props.platformApp &&
    !props.isMobileNav &&
    css`
      ${up('lg')} {
        padding: 12.5px 30px;
      }
    `}

  .dropdown-caret {
    flex: 0 0 auto;
    font-size: 18px;
    margin-left: 6px;
  }
`;

const ChildContainer = styled.ul`
  margin: 0px 0px !important;
  padding: 0px;
  position: absolute;
  top: 100%;
  list-style-type: none;
  width: max-content;
  min-width: 100%;
  overflow: hidden;
  border-radius: 0px 0px 4px 4px;
  background: ${(props) => props.theme.colors.black2};
`;

const SubListItem = styled.li`
  list-style: none;
  width: 100%;
  & > a {
    animation: ${ItemAnimation} 0.2s;
    width: 100%;
    display: flex;
    align-items: center;
    cursor: default;
    min-width: 56px;
    font-weight: 700;
    letter-spacing: 1px;
    color: ${(props) => props.theme.colors.white};
    padding: 13px 21px;
    margin-left: 0px;
    box-sizing: border-box;
    ${(props) =>
      props.isMobileNav &&
      css`
        color: ${props.theme.colors.black};
        font-weight: 400;
        justify-content: flex-start;
        min-height: 34px;
      `}
    ${(props) =>
      !props.isMobileNav &&
      css`
        background: ${props.theme.colors.black2};
      `}
    &:hover {
      background: ${(props) => props.theme.colors.yellow};
      ${(props) =>
        props.isMobileNav &&
        css`
          background: ${props.theme.colors.white};
          color: ${props.theme.colors.black};
          box-shadow: none !important;
          font-weight: 400;
          transition: background 0.3s;

          span {
            background: ${props.theme.colors.gray17};
            color: ${props.theme.colors.black};
            border-radius: 4px;
          }
        `}
    }
    span {
      width: 100%;
      display: block;
      overflow: hidden;
      white-space: nowrap;
      text-align: left;
      font-size: 14px;
      ${(props) =>
        props.isMobileNav &&
        css`
          display: flex;
          width: 100%;
          height: auto;
          margin-left: 0px;
          padding: 7px 20px 7px 46px !important;
        `}
      ${(props) =>
        props.active &&
        !props.isMobileNav &&
        css`
          border-color: ${props.theme.colors.black2};

          background: ${props.theme.colors.black2};
          color: ${props.theme.colors.white};
          font-weight: 600;
          transition: background 0.3s;
        `}
    }
  }
`;

const ListItem = styled.li`
  box-sizing: border-box;
  border-radius: 1px;
  list-style: none;
  position: relative;
  margin-right: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0px;
  // transition: height, 1s;

  @media (max-width: 960px) {
    a {
      height: 60px; /* Set a fixed height for <a> on screens below 960px */
    }
  }

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: 100%;
    cursor: default;

    span {
      letter-spacing: 1px;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: left;
      height: 100%;
      letter-spacing: 1px;
      box-sizing: border-box;
      cursor: default;
    }
  }

  &:hover {
    border-color: ${(props) => props.theme.colors.black2};
    background-color: ${(props) => props.theme.colors.white};
    transition: all 0.3s;
    ${StyledTag} {
      background: ${(props) => props.theme.colors.black2};
      color: ${(props) => props.theme.colors.white};
      box-shadow: ${(props) =>
        props.isMobileNav
          ? 'none'
          : `0px 4px 14px ${props.theme.colors.gray11}`} !important;
      border-radius: ${(props) =>
        props.hasChildren ? '4px 4px 0px 0px' : '4px'};
      font-weight: 600;
      transition: background 0.3s;
    }
  }
  ${(props) =>
    props.active &&
    css`
      border-color: ${props.theme.colors.black2};

      ${StyledTag} {
        background: ${props.theme.colors.black2};
        color: ${props.theme.colors.white};
        box-shadow: ${props.isMobileNav
          ? 'none'
          : `0px 4px 14px ${props.theme.colors.gray11}`} !important;
        border-radius: 4px;
        font-weight: 600;
        transition: background 0.3s;
      }
    `}

  ${ChildContainer} {
    display: none;
  }

  &:hover {
    z-index: ${(props) => (props.isMobileNav ? 0 : 10)};

    ${ChildContainer} {
      display: block;
    }
  }

  ${(props) =>
    props.isMobileNav &&
    css`
     @media screen {
      display: flex;
      flex-direction: column;
      height: auto;
      align-items: stretch;
      margin-right: 0px;
      width: 100%;

      & > a {
        height: auto;
        min-height: 36px;
      }

      ${StyledTag} {
        background: ${props.theme.colors.white};
        color: ${props.theme.colors.black};
        border-radius: 0px;
        justify-content: flex-start;
        border-left: 4px solid transparent;
        padding: ${props.hasChildren ? '10px 20px 6px' : '8px 20px'};
        font-weight: 700 !important;
      }

      &:not(:first-child) {
        margin-top: ${props.hasChildren ? '2px' : '0px'};
      }

      &:hover {
        background-color: ${props.theme.colors.white};

        ${StyledTag} {
          background: ${props.theme.colors.gray17};
          color: ${props.theme.colors.black};
          box-shadow: none !important;
          border-radius: 0px;
          border-left-color: ${props.theme.colors.yellow5};
        }
      }

    ${ChildContainer} {
      display: block;
      position: relative;
      top: 0;
      width: 100%;
      background: transparent;
      border-radius: 0px;
      overflow: visible;
     }
  `}

  ${(props) =>
    !props.isClickable &&
    css`
      cursor: default;
      & > a {
        pointer-events: none;
      }
    `}
`;

export function NavigationItem(componentProps) {
  const { platformApp, language } = useContext(AppContext);

  const {
    children,
    to,
    hash,
    childItems = [],
    onItemHover,
    index,
    showBorder,
    isMobileNav,
    hideDesktopNav,
    disabled,
  } = componentProps;

  const [activeLink, setActiveLink] = useState(null);
  const location = useLocation();
  const parentLink = hash && to ? `${to}${hash}` : to;
  const hasChildren = childItems.length > 0;

  const getChildLink = (item) => {
    const childTitle = String(item.title).toLowerCase().trim();
    const childHash =
      to === '/getting-started' && childTitle.includes('overview')
        ? overviewTimelineHash
        : item.hash || '';

    return `${to || ''}${item.to || ''}${childHash}` || '/';
  };

  useEffect(() => {
    // Split the pathname to get the individual parts
    const parts = location.pathname.split('/');
    const parentName = parts.length > 2 ? parts[2] : '';

    // Construct the full URL for each child and find the active child
    const activeChild = childItems.find(
      (child) => `/${parts[1]}/${parentName}${child?.to}` === location.pathname,
    );

    // If isMobileNav is true, we want to maintain the parent name as the active link
    if (isMobileNav) {
      if (location.pathname === `/${language}${to}`) {
        setActiveLink(componentProps);
      } else {
        setActiveLink(null);
      }
    }
    // If isMobileNav is false, we want to maintain the child name as the active link
    else if (activeChild) {
      setActiveLink(activeChild);
    } else if (location.pathname === `/${language}${to}`) {
      setActiveLink(componentProps);
    } else {
      setActiveLink(null);
    }
  }, [location, childItems, componentProps, to, language, isMobileNav]);

  return (
    <ListItem
      hideDesktopNav={hideDesktopNav}
      active={
        location.pathname === `/${language}${to}` ||
        location.pathname === `/${language}${activeLink?.to}`
      }
      onMouseEnter={() => {
        if (onItemHover) {
          onItemHover(index);
        }
      }}
      showBorder={showBorder}
      isMobileNav={isMobileNav}
      isClickable={!disabled}
      hasChildren={hasChildren}
    >
      <Link to={parentLink}>
        <StyledTag
          isMobileNav={isMobileNav}
          isActive={activeLink}
          platformApp={platformApp}
        >
          {children}
          {hasChildren && !isMobileNav && (
            <ChevronDown aria-hidden="true" className="dropdown-caret" />
          )}
        </StyledTag>
      </Link>
      {hasChildren && (
        <ChildContainer>
          {childItems.map((item, i) => {
            const childLink = getChildLink(item);

            return (
              <SubListItem
                key={i}
                isMobileNav={isMobileNav}
                active={location.pathname === `/${language}${to}${item.to}`}
              >
                <Link to={childLink}>
                  <span>{item.title}</span>
                </Link>
              </SubListItem>
            );
          })}
        </ChildContainer>
      )}
    </ListItem>
  );
}
